<template>
	<div
		class="leaderboard-row"
		:class="`leaderboard-row--${skin || 'transparent'}`"
	>
		<HorizontalDecorSidesCrackedBg v-if="skin" :class="decorBgClass" />
		<div class="leaderboard-row__rank">
			<ShrinkText
				:text="typeof rank === 'number' ? formatNumber(rank) : rank"
				:fontSize="1"
				containerClassList="leaderboard-row__rank-container"
			/>
		</div>
		<div class="leaderboard-row__country">
			<div :class="`flag--${country.toLowerCase()}-22`" />
		</div>
		<div class="leaderboard-row__name">
			{{ name }}
		</div>
		<div class="leaderboard-row__score">
			{{ typeof score === "number" ? formatNumber(score) : score }}
			<AppMainIcon
				class="leaderboard-row__score-icon"
				:iconName="icon"
				:iconSize="16"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import { ResourceType } from "@/game/entities/config/resources";
import type { SkinTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { computed } from "vue";
import ShrinkText from "../ShrinkText.vue";
import HorizontalDecorSidesCrackedBg from "../SVG/HorizontalDecorSidesCrackedBg.vue";

interface Props {
	rank: number | string;
	country: string;
	name: string;
	score: number | string;
	skin?: SkinTypes | null;
	icon?: ResourceType;
}

const {
	rank,
	country,
	name,
	score,
	skin,
	icon = ResourceType.COINS,
} = defineProps<Props>();

const decorBgClass = computed((): string => {
	if (skin) {
		return `horizontal-decor-sides-cracked-bg--${skin}`;
	}
	return "";
});
</script>

<style lang="scss" scoped>
.leaderboard-row {
	width: 100%;
	@include flex-row-between;
	position: relative;
	gap: 1rem;
	margin: 0;
	padding: 0.375rem 0 0.5rem;
	height: 100%;

	&--light {
		color: #372615;

		&::before {
			position: absolute;
			top: 0;
			left: 1rem;
			z-index: -1;
			background: hsl(43, 44%, 36%);
			width: calc(100% - 2rem);
			height: 100%;
			content: "";
		}

		.leaderboard-row__name {
			font-weight: 800;
		}
	}

	&--dark,
	&--transparent {
		color: #fff3dd;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	&__country {
		margin-bottom: -0.125rem;
		width: 1.5rem;
	}

	&__rank {
		margin: 0 0.5rem;
		width: 2.5rem;
		@include flex-row-center;
		&-container {
			width: 100%;
			height: 100%;
		}
	}

	&__name {
		flex: 1;
	}

	&__score {
		margin: 0 0.625rem;
	}

	&__score-icon {
		margin-bottom: -0.25rem;
	}

	& > *:not(.horizontal-decor-sides-cracked-bg) {
		z-index: 1;
	}

	.horizontal-decor-sides-cracked-bg {
		position: absolute;
		top: 0;
		left: 0;
		// correct handling of the base layer (0) for the row's bg decorations
		z-index: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;

		:deep(svg) {
			position: absolute;
			top: -5%;
			width: 100%;
			height: 110%;
		}
	}
}
</style>
